import React from "react";
import { motion } from "framer-motion";
import { FiArrowUpRight, FiGithub, FiLock, FiUsers } from "react-icons/fi";
import { useLanguage } from "../../i18n/LanguageContext";
import { featuredProjects, secondaryProjects } from "../../data/projects";
import { Reveal } from "../common/Reveal";
import { Icon } from "../common/Icon";
import "./Projects.css";

export function Projects() {
  const { t } = useLanguage();

  return (
    <section id="projects" className="section projects">
      <div className="container section-inner">
        <Reveal direction="up">
          <span className="eyebrow">{t.projects.eyebrow}</span>
          <h2 className="section-heading">{t.projects.heading}</h2>
          <p className="section-sub">{t.projects.subheading}</p>
        </Reveal>

        <div className="featured-projects-list">
          {featuredProjects.map((project, index) => {
            const f = t.projects.featuredProjects[project.id];
            return (
              <Reveal direction="up" delay={0.15 * (index + 1)} key={project.id}>
                <motion.article className="featured-project" whileHover="hover" initial="rest" animate="rest">
                  <div className="featured-project__info">
                    <span className="tag featured-project__badge">{t.projects.featuredLabel}</span>
                    <h3 className="featured-project__name">{f.name}</h3>
                    <p className="featured-project__tagline">{f.tagline}</p>
                    <p className="featured-project__desc">{f.description}</p>

                    <div className="featured-project__meta">
                      <span className="featured-project__status">
                        <span className="featured-project__status-dot" />
                        {f.status}
                      </span>
                    </div>

                    <div className="featured-project__stack">
                      {project.stack.map((tech) => (
                        <span className="tag" key={tech}>
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="featured-project__actions">
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary featured-project__action-btn"
                          data-cursor="hover"
                        >
                          {f.liveLabel} <Icon icon={FiArrowUpRight} />
                        </a>
                      )}
                      {project.backendUrl && (
                        <a
                          href={project.backendUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary featured-project__action-btn"
                          data-cursor="hover"
                        >
                          <Icon icon={FiGithub} /> {t.projects.backendLabel} <Icon icon={FiArrowUpRight} />
                        </a>
                      )}
                      {project.isPrivate && f.privateLabel && (
                        <span className="tag featured-project__private">
                          <Icon icon={FiLock} /> {f.privateLabel}
                        </span>
                      )}
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="tag featured-project__domain"
                          data-cursor="hover"
                        >
                          <span className="featured-project__domain-dot" />
                          {f.domainLabel}
                          <span className="featured-project__domain-note">{f.domainNote}</span>
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="featured-project__preview">
                    <motion.a
                      href={project.liveUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mockup mockup--link"
                      variants={{ rest: { y: 0 }, hover: { y: -8 } }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                      data-cursor="hover"
                      title={`${f.name} — ${f.liveLabel}`}
                    >
                      <div className="mockup__bar">
                        <span className="mockup__dot" />
                        <span className="mockup__dot" />
                        <span className="mockup__dot" />
                        <span className="mockup__url mono">{f.domainLabel}</span>
                      </div>
                      <div className="mockup__frame">
                        <img
                          src={project.previewImage}
                          alt={`${f.name} — ${f.previewNote}`}
                          className="mockup__image"
                        />
                      </div>
                    </motion.a>
                    <span className="featured-project__preview-note mono">{f.previewNote}</span>
                  </div>
                </motion.article>
              </Reveal>
            );
          })}
        </div>

        <Reveal direction="up" delay={0.2}>
          <span className="secondary-projects__label mono">{t.projects.secondaryLabel}</span>
        </Reveal>

        <div className="secondary-projects">
          {secondaryProjects.map((project, i) => {
            const copy = t.projects.secondary[project.id];
            return (
              <Reveal direction="up" delay={0.08 * i} key={project.id}>
                <motion.article className="secondary-project" whileHover={{ y: -4 }}>
                  <div className="secondary-project__head">
                    <h4 className="secondary-project__name">{copy.name}</h4>
                    <span className="secondary-project__status mono">{copy.status}</span>
                  </div>
                  <p className="secondary-project__tagline">{copy.tagline}</p>
                  <p className="secondary-project__desc">{copy.description}</p>

                  {copy.teamNote && (
                    <span className="secondary-project__team">
                      <Icon icon={FiUsers} /> {copy.teamNote}
                    </span>
                  )}

                  <div className="secondary-project__stack">
                    {project.stack.map((tech) => (
                      <span className="tag" key={tech}>
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="secondary-project__actions">
                    {project.liveUrl && (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="secondary-project__link"
                        data-cursor="hover"
                      >
                        <Icon icon={FiArrowUpRight} /> {t.projects.liveLabel}
                      </a>
                    )}
                    {project.repoUrl && (
                      <a
                        href={project.repoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="secondary-project__link"
                        data-cursor="hover"
                      >
                        <Icon icon={FiGithub} /> {t.projects.codeLabel} <Icon icon={FiArrowUpRight} />
                      </a>
                    )}
                    {project.backendUrl && (
                      <a
                        href={project.backendUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="secondary-project__link"
                        data-cursor="hover"
                      >
                        <Icon icon={FiGithub} /> {t.projects.backendLabel} <Icon icon={FiArrowUpRight} />
                      </a>
                    )}
                    {project.frontendUrl && (
                      <a
                        href={project.frontendUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="secondary-project__link"
                        data-cursor="hover"
                      >
                        <Icon icon={FiGithub} /> {t.projects.frontendLabel} <Icon icon={FiArrowUpRight} />
                      </a>
                    )}
                  </div>
                </motion.article>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
